import { useState } from 'react'
import { MessagesSquare } from 'lucide-react'
import type { Thread } from '../types'
import { useThreads } from '../hooks'
import { ThreadList } from './ThreadList'
import { ThreadView } from './ThreadView'
import { NewThreadDialog } from './NewThreadDialog'

/**
 * Dwupanelowy widok "Dyskusje" (master-detail).
 * Lewy panel: lista wątków dla danego `entityRef`. Prawy: wybrany wątek
 * (lub placeholder). `NewThreadDialog` tworzy nowy wątek i od razu go zaznacza.
 *
 * `currentUserId` dostarcza strona-host (np. ContractDetailPage z useAuthStore).
 * Nie ma endpointu get-thread-by-id, więc wybrany wątek wyprowadzamy z cache listy
 * (`useThreads` bez filtra = wszystkie statusy). Trzymanie snapshotu w stanie
 * powodowało, że prawy panel nie reagował na zmianę statusu (toggle/realtime)
 * aż do odświeżenia strony. `seed` mostkuje tylko świeżo utworzony wątek,
 * zanim invalidacja dociągnie go do cache.
 */
export function DiscussionsTab({ entityRef, currentUserId }: {
  entityRef: string
  currentUserId: number
}) {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [seed, setSeed] = useState<Thread | null>(null)
  const [newThreadOpen, setNewThreadOpen] = useState(false)

  const { data: threads } = useThreads(entityRef)
  const selected =
    selectedId == null
      ? null
      : threads?.find(t => t.id === selectedId) ?? (seed?.id === selectedId ? seed : null)

  return (
    <div className="flex h-full w-full bg-white">
      <div className="w-[300px] shrink-0">
        <ThreadList
          entityRef={entityRef}
          selectedId={selectedId}
          onSelect={(t) => { setSeed(null); setSelectedId(t.id) }}
          onNewThread={() => setNewThreadOpen(true)}
        />
      </div>
      <div className="flex-1 min-w-0">
        {selected ? (
          <ThreadView thread={selected} currentUserId={currentUserId} />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <MessagesSquare className="h-10 w-10 text-slate-200" />
            <p className="text-sm text-slate-400">Wybierz wątek z listy po lewej</p>
          </div>
        )}
      </div>
      <NewThreadDialog
        entityRef={entityRef}
        open={newThreadOpen}
        onClose={() => setNewThreadOpen(false)}
        onCreated={(t) => { setSeed(t); setSelectedId(t.id) }}
      />
    </div>
  )
}
